import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePrimaryComplaintStore, ComplaintHistoryStep } from "@/store/primaryComplaint";
import { complaintHistoryLabels as t } from "./complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { ChoiceGroup } from "@/components/forms/ChoiceGroup";
import { ComplaintStepHeader, StepLoadError } from "./ComplaintStepHeader";
import { useComplaintValues } from "@/hooks/useComplaintValues";

const StartOptions = [
  "suddenlyAllAtOnce",
  "graduallyOverTime",
  "afterAnAccident",
  "afterAnInjury",
  "unknownNotSure",
] as const;

const FrequencyOptions = [
  "constantAlwaysPresent",
  "occasionalAFewTimesPerWeek",
  "rareLessThanOncePerWeek",
  "intermittentComesAndGoes",
] as const;

const HistoryFirstStepFields = {
  Onset: "Onset",
  ModeOfOnset: "ModeOfOnset",
  ModeOfOnsetDetail: "ModeOfOnsetDetail",
  Frequency: "Frequency",
} as const;

type ComplaintHistoryAnswers = {
  Onset: string;
  ModeOfOnset: string;
  ModeOfOnsetDetail: string;
  Frequency: string;
};

/** Onset choices. Today / Yesterday are stored as ISO dates; the rest as their label. */
const ONSET_CHOICES = ["today", "yesterday", "thisWeek", "weeksAgo", "monthsAgo", "yearsAgo", "exact"] as const;
type OnsetChoice = (typeof ONSET_CHOICES)[number];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The patient's LOCAL calendar date as YYYY-MM-DD. (toISOString is UTC: after ~8pm in US time
 *  zones it is already tomorrow, so "Today" would record the wrong onset date.) */
function isoDate(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Which onset choice a stored value belongs to (an ISO date that isn't today/yesterday is "exact"). */
function onsetChoiceFor(value: string, today: string, yesterday: string): OnsetChoice | null {
  if (!value) return null;
  if (value === today || value === "today") return "today";
  if (value === yesterday || value === "yesterday") return "yesterday";
  if (ISO_DATE.test(value)) return "exact";
  const match = (["thisWeek", "weeksAgo", "monthsAgo", "yearsAgo"] as const).find((k) => t[k] === value);
  return match ?? null;
}

/** Some older rows stored the option key (e.g. "suddenlyAllAtOnce") instead of its label. */
function labelForStoredOption(value: string): string {
  const key = [...StartOptions, ...FrequencyOptions].find((k) => k === value);
  return key ? t[key] : value;
}

export function ComplaintHistory1() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const complaintIdParam = searchParams.get("complaintId");
  const patientIDParam = searchParams.get("patientID");

  const [answers, setAnswers] = useState<ComplaintHistoryAnswers>({
    Onset: "",
    ModeOfOnset: "",
    ModeOfOnsetDetail: "",
    Frequency: "",
  });
  const [onsetChoice, setOnsetChoice] = useState<OnsetChoice | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Last description known to be on the server, so clearing it saves "" but an untouched empty box doesn't.
  const savedDetail = useRef("");

  const updateComplaintValue = usePrimaryComplaintStore((s) => s.updateComplaintValue);
  const setComplaintHistoryStep = usePrimaryComplaintStore((s) => s.setComplaintHistoryStep);
  const { values, status, retry } = useComplaintValues(complaintIdParam);

  const todayDate = isoDate(0);
  const yesterdayDate = isoDate(1);

  useEffect(() => {
    if (status !== "ready" || !values) return;
    const next: ComplaintHistoryAnswers = { Onset: "", ModeOfOnset: "", ModeOfOnsetDetail: "", Frequency: "" };
    for (const v of values) {
      if (v.code === HistoryFirstStepFields.Onset) {
        // Legacy rows stored the words "today" / "yesterday"; show them as the matching date.
        next.Onset = v.value === "today" ? todayDate : v.value === "yesterday" ? yesterdayDate : v.value;
      } else if (v.code in next) {
        (next as Record<string, string>)[v.code] = labelForStoredOption(v.value ?? "");
      }
    }
    savedDetail.current = next.ModeOfOnsetDetail;
    setAnswers(next);
    setOnsetChoice(onsetChoiceFor(next.Onset, todayDate, yesterdayDate));
  }, [status, values, todayDate, yesterdayDate]);

  /** Record an answer and save it right away; the patient chose it, so it's a real answer. */
  const updateValue = (field: keyof ComplaintHistoryAnswers, value: string) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
    void updateComplaintValue(field, value).catch(() => {});
  };

  const selectOnset = (choice: string | null) => {
    const c = choice as OnsetChoice | null;
    setOnsetChoice(c);
    if (c === "today") updateValue(HistoryFirstStepFields.Onset, todayDate);
    else if (c === "yesterday") updateValue(HistoryFirstStepFields.Onset, yesterdayDate);
    else if (c === "exact") {
      // Wait for the date itself; keep an already-saved exact date.
      if (!ISO_DATE.test(answers.Onset)) setAnswers((prev) => ({ ...prev, Onset: "" }));
    } else if (c) updateValue(HistoryFirstStepFields.Onset, t[c]);
  };

  const goBack = () => {
    const params = new URLSearchParams();
    if (complaintIdParam) params.set("complaintId", complaintIdParam);
    if (patientIDParam) params.set("patientID", patientIDParam);
    const qs = params.toString();
    navigate(`/complaint/primary-complaint${qs ? `?${qs}` : ""}`);
  };

  const saveAndContinue = async () => {
    // Only once saved answers have loaded; otherwise the form may not reflect what's saved.
    if (status !== "ready") return;
    // Re-save every answered field and wait, so nothing depends on an earlier fire-and-forget save.
    // Unanswered fields are never written: an empty answer is not a patient answer.
    // A cleared description is a real change, so it is re-saved too.
    const answered = (Object.keys(answers) as (keyof ComplaintHistoryAnswers)[]).filter(
      (k) =>
        answers[k].trim() !== "" ||
        (k === HistoryFirstStepFields.ModeOfOnsetDetail && answers[k] !== savedDetail.current)
    );
    setIsSaving(true);
    try {
      await Promise.all(answered.map((k) => updateComplaintValue(k, answers[k])));
    } catch {
      // updateComplaintValue already shows a toast; stay so the patient can retry.
      setIsSaving(false);
      return;
    }
    setIsSaving(false);
    setComplaintHistoryStep(ComplaintHistoryStep.ComplaintHistory2);
  };

  // Track the description locally as they type; persist on blur. Saving per keystroke sends
  // overlapping requests that can land out of order and overwrite the final text.
  const handleDetailBlur = () => {
    const detail = answers.ModeOfOnsetDetail;
    if (detail === savedDetail.current) return;
    savedDetail.current = detail;
    void updateComplaintValue(HistoryFirstStepFields.ModeOfOnsetDetail, detail).catch(() => {});
  };

  /** iOS WebKit often paints the caret late; nudge layout + scroll so focus reads as "active". */
  const handleDetailTextareaFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    requestAnimationFrame(() => {
      void el.offsetHeight;
      el.scrollIntoView({ block: "nearest", inline: "nearest" });
    });
  };

  const onsetOptions = ONSET_CHOICES.map((c) => ({
    value: c,
    label: c === "exact" ? t.exactDate : t[c],
  }));

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content p-4 flex flex-col">
        <div className="complaint-history-card">
          <ComplaintStepHeader complaintId={complaintIdParam} step="history1" />

          {status === "loading" && (
            <div className="wizard-skeleton" role="status" aria-busy="true" aria-label="Loading your answers">
              <span />
              <span />
              <span />
            </div>
          )}

          {status === "error" && <StepLoadError onRetry={retry} />}

          {status === "ready" && (
            <>
              <ChoiceGroup
                id="onset"
                legend={t.complaintHistoryWhenLabel}
                options={onsetOptions}
                value={onsetChoice}
                onChange={selectOnset}
              />
              {onsetChoice === "exact" && (
                <div className="choice-group">
                  <label htmlFor="onset-date" className="choice-textarea-label">
                    {t.exactDateLabel}
                  </label>
                  <input
                    id="onset-date"
                    type="date"
                    className="complaint-history-date-input"
                    max={todayDate}
                    value={ISO_DATE.test(answers.Onset) ? answers.Onset : ""}
                    onChange={(e) => e.target.value && updateValue(HistoryFirstStepFields.Onset, e.target.value)}
                  />
                </div>
              )}

              <ChoiceGroup
                id="mode-of-onset"
                legend={t.complaintHistoryHowLabel}
                options={StartOptions.map((o) => ({ value: t[o], label: t[o] }))}
                value={answers.ModeOfOnset || null}
                onChange={(v) => v && updateValue(HistoryFirstStepFields.ModeOfOnset, v)}
              />

              <div className="choice-group">
                <label htmlFor="complaint-history-onset-detail" className="choice-textarea-label">
                  {t.onsetDetailLabel} <span className="choice-group__hint">{t.optional}</span>
                </label>
                <textarea
                  id="complaint-history-onset-detail"
                  className="complaint-history-textarea"
                  name="modeOfOnsetDetail"
                  maxLength={500}
                  placeholder={t.onsetDetailPlaceholder}
                  value={answers.ModeOfOnsetDetail}
                  onChange={(e) =>
                    setAnswers((prev) => ({ ...prev, ModeOfOnsetDetail: e.target.value.slice(0, 500) }))
                  }
                  onBlur={handleDetailBlur}
                  onFocus={handleDetailTextareaFocus}
                  enterKeyHint="done"
                />
                <p className="complaint-history-char-count">{answers.ModeOfOnsetDetail.length} / 500</p>
              </div>

              <ChoiceGroup
                id="frequency"
                legend={t.complaintHistoryHowOftenLabel}
                options={FrequencyOptions.map((o) => ({ value: t[o], label: t[o] }))}
                value={answers.Frequency || null}
                onChange={(v) => v && updateValue(HistoryFirstStepFields.Frequency, v)}
              />
            </>
          )}
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack} aria-label={t.backButton}>
          ← {t.backButton}
        </Button>
        <Button type="button" onClick={saveAndContinue} disabled={isSaving || status !== "ready"}>
          {isSaving ? "Saving…" : t.continueButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
